import { NgClass, NgStyle } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { HealthLabsCouponComponent } from '@components/health-labs-coupon/health-labs-coupon.component';
import { OrderCompleteInstructionsComponent } from '@components/order-complete/order-complete-instructions/order-complete-instructions.component';
import { OrderCompleteSummaryComponent } from '@components/order-complete/order-complete-summary/order-complete-summary.component';
import { ShopperApprovedComponent } from '@components/shopper-approved/shopper-approved.component';
import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { environment } from '@environments/environment';
import { OrderCompleteDetails } from '@models/order-complete-details';
import { OrderSummary } from '@models/order-summary';
import { ReviewsFeature } from '@models/reviews-feature';
import { DataLayerService } from '@services/data-layer.service';
import { ErrorLoggingService } from '@services/error-logging.service';
import { OrderService } from '@services/order.service';
import { StorageService } from '@services/storage.service';

@Component({
  selector: 'app-order-complete',
  templateUrl: './order-complete.component.html',
  imports: [
    OrderCompleteInstructionsComponent,
    ShopperApprovedComponent,
    NgStyle,
    OrderCompleteSummaryComponent,
    NgClass,
    HealthLabsCouponComponent,
  ],
})
export class OrderCompleteComponent implements OnInit {
  public orderCompleteDetails: OrderCompleteDetails;
  public displayShopperApprovedCard: boolean = false;
  public treatmentType: ConsultationTreatmentTypes;

  private dataLayerService = inject(DataLayerService);
  private orderService = inject(OrderService);
  private storage = inject(StorageService);
  private errorLoggingService = inject(ErrorLoggingService);

  /**
   * Determines if the Shopper Approved card should be displayed.
   */
  public get shouldDisplayShopperApproved(): boolean {
    return !!environment.shopperApproved.site && !!environment.shopperApproved.token;
  }

  /**
   * Determines if the sister site banner should be displayed.
   */
  public get shouldDisplaySisterSiteBanner(): boolean {
    return environment.order.orderComplete.displaySisterSiteBanner;
  }

  /**
   * Initializes the component.
   */
  public ngOnInit(): void {
    if (this.shouldDisplayShopperApproved) {
      this.observeShopperApproved();
    }

    this.treatmentType = this.storage.treatmentType;
    this.dataLayerService.handleDataLayer();
    this.orderCompleteDetails = this.getOrderCompleteDetails();
    this.markOrderCompleted();
  }

  /**
   * Dispatches the request that tells API the order is complete.
   *
   * This request will trigger the logic in the API side that will make sure the requisition is in place.
   */
  public markOrderCompleted(): void {
    this.orderService.orderCompleted().subscribe(
      () => this.orderService.clearLastOrder(),
      (error) => {
        this.errorLoggingService.logError(error);
        this.orderService.clearLastOrder();
      },
    );
  }

  /**
   * Consolidate and return all the order details necessary for the order summary component and shopper approved.
   */
  private getOrderCompleteDetails(): OrderCompleteDetails {
    let payments = [];
    payments.push(this.storage.transactionId);

    if (this.storage.upsell.length > 0) {
      payments = payments.concat(this.storage.upsell);
    }

    if (this.storage.secondaryPaymentTransactionId) {
      payments.push(this.storage.secondaryPaymentTransactionId);
    }

    const summary = new OrderSummary(
      this.storage.orderedTests.map((test) => test.name),
      payments,
      this.storage.orderTotal,
    );

    const reviewsFeature = new ReviewsFeature(
      this.storage.transactionId,
      this.storage.patient?.name,
      this.storage.patient?.email,
    );

    return new OrderCompleteDetails(
      summary,
      this.storage.transactionId,
      this.storage.free,
      reviewsFeature,
      this.storage.sendSMS,
      this.storage.hasEmail,
      this.storage.paymentType == 'payLater',
      this.storage.order.hash,
      this.storage.inHomeBookingResult,
      this.storage.email,
    );
  }

  /**
   * Watch for mutations in the shopperApprovedContainer div and displays the
   * Shopper Approved card when the element with the id shopper_approved is
   * found.
   */
  private observeShopperApproved(): void {
    const observer = new MutationObserver((mutations) => {
      if (
        (this.displayShopperApprovedCard = mutations
          .map((m) => Array.from(m.addedNodes))
          .flat()
          .map((n) => (n as any).id)
          .includes('shopper_approved'))
      ) {
        observer.disconnect();
      }
    });

    observer.observe(document.getElementById('shopperApprovedContainer'), {
      childList: true,
      subtree: true,
    });
  }
}
